import React from "react";
import { Switch } from "../../atoms/Switch";
import { CodeBlock } from "../../molecules/CodeBlock";
import { SegmentedPicker } from "../../molecules/SegmentedPicker";
import { SettingRow, SettingsGroup } from "../../molecules/SettingRow";
import { LivePreviewFrame } from "../../molecules/LivePreviewFrame";
import { embedPreviewSrc } from "../../../services/shareLinks/embedPreviewSrc";
import { DurationPicker } from "../../molecules/DurationPicker";
import { ShareLinkActions } from "../ShareLinkActions";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import type { ShareLinkOptions } from "../../../services/shareLinks/shareLinks.types";
import { EMBED_PANEL_COPY as C } from "./EmbedSettingsPanel.copy";
import type { EmbedSettingsPanelProps } from "./EmbedSettingsPanel.types";
import { Column, Control, Layout, StickyColumn } from "./EmbedSettingsPanel.styles";

const SOON: { key: keyof typeof C.comingSoon; group: "access" | "actions" | "data" | "look" }[] = [
  { key: "workspaceAccess", group: "access" },
  { key: "allowedSites", group: "access" },
  { key: "workspaceLogo", group: "look" },
  { key: "presetFilters", group: "data" },
  { key: "readOnlyFilters", group: "data" },
  { key: "download", group: "actions" },
  { key: "explanations", group: "actions" },
  { key: "askAi", group: "actions" },
  { key: "viewData", group: "actions" },
  { key: "freshness", group: "data" },
  { key: "updatedStamp", group: "data" },
  { key: "autoFit", group: "data" },
];

const soonRows = (group: string) =>
  SOON.filter((s) => s.group === group).map((s) => (
    <SettingRow key={s.key} label={C.comingSoon[s.key]} comingSoon>
      <Switch value={false} onChange={() => undefined} disabled ariaLabel={C.comingSoon[s.key]} />
    </SettingRow>
  ));

/** Embed settings (Access, Look, What viewers can do, Data) with a live iframe preview and the embed code. */
export const EmbedSettingsPanel: React.FC<EmbedSettingsPanelProps> = ({ link, onChange, onRevoke, onReset, previewToken, disabled = false, origin, className }) => {
  const { copiedKey, copy } = useCopyToClipboard();
  const base = origin ?? window.location.origin;
  const url = link.slug ? `${base}/e/${link.slug}` : null;
  const code = url ? `<iframe src="${url}" width="100%" height="600" frameborder="0" allowfullscreen></iframe>` : "";
  const on = link.access === "public";
  const o = link.options;
  const setOpt = <K extends keyof ShareLinkOptions>(k: K, v: ShareLinkOptions[K]) =>
    onChange({ access: link.access, expires_at: link.expires_at, options: { ...o, [k]: v } });
  const toggle = (k: "header" | "nav" | "chrome" | "powered_by" | "fullscreen", label: string, extra?: boolean) => (
    <Switch value={o[k]} onChange={(v) => setOpt(k, v)} disabled={disabled || !on || extra} ariaLabel={label} />
  );
  const off = disabled || !on;
  const previewSrc = embedPreviewSrc(base, previewToken, o);
  const codeShown = on && code ? code : C.codeLocked;

  return (
    <Layout className={className}>
      <Column>
        <SettingsGroup title={C.access}>
          <SettingRow label={C.enable} description={C.enableHint}>
            <Switch
              value={on}
              disabled={disabled}
              ariaLabel={C.enable}
              onChange={(v) => onChange({ access: v ? "public" : "restricted", expires_at: link.expires_at, options: o })}
            />
          </SettingRow>
          <SettingRow label={C.lifetime} stacked>
            <DurationPicker value={link.expires_at} disabled={off} onChange={(iso) => onChange({ access: link.access, expires_at: iso, options: o })} />
          </SettingRow>
          {soonRows("access")}
        </SettingsGroup>
        <SettingsGroup title={C.look}>
          <SettingRow label={C.chrome} description={C.chromeHint}>{toggle("chrome", C.chrome)}</SettingRow>
          <SettingRow label={C.header}>{toggle("header", C.header, !o.chrome)}</SettingRow>
          <SettingRow label={C.nav}>{toggle("nav", C.nav, !o.chrome)}</SettingRow>
          <SettingRow label={C.poweredBy}>{toggle("powered_by", C.poweredBy, !o.chrome)}</SettingRow>
          <SettingRow label={C.logo} stacked>
            <SegmentedPicker
              ariaLabel={C.logo}
              variant="form"
              value={o.logo}
              onChange={(id) => !off && setOpt("logo", id as ShareLinkOptions["logo"])}
              options={[
                { id: "magicbi", label: C.logoMagicbi, disabled: off },
                { id: "none", label: C.logoNone, disabled: off },
              ]}
            />
          </SettingRow>
          <SettingRow label={C.theme} stacked>
            <SegmentedPicker
              ariaLabel={C.theme}
              variant="form"
              value={o.theme}
              onChange={(id) => !off && setOpt("theme", id as ShareLinkOptions["theme"])}
              options={[
                { id: "light", label: C.themeLight, disabled: off },
                { id: "dark", label: C.themeDark, disabled: off },
                { id: "auto", label: C.themeAuto, disabled: off },
              ]}
            />
          </SettingRow>
          {soonRows("look")}
        </SettingsGroup>
        <SettingsGroup title={C.actions}>
          <SettingRow label={C.fullscreen}>{toggle("fullscreen", C.fullscreen)}</SettingRow>
          {soonRows("actions")}
        </SettingsGroup>
        <SettingsGroup title={C.data}>{soonRows("data")}</SettingsGroup>
      </Column>
      <StickyColumn>
        <LivePreviewFrame heading={C.preview} title={C.previewTitle} src={previewSrc} emptyText={C.previewEmpty} />
        <Control as="div" style={{ minWidth: 0 }}>
          <CodeBlock
            code={codeShown}
            disabled={!on || !code}
            label={C.code}
            ariaLabel={C.code}
            onCopy={() => (on && code ? void copy(code, "embed") : undefined)}
            copyLabel={C.copy}
            copiedLabel={C.copied}
            copied={copiedKey === "embed"}
          />
        </Control>
        {onRevoke ? (
          <ShareLinkActions onRevoke={onRevoke} onReset={onReset} disabled={disabled || !link.slug} disabledReason={link.slug ? undefined : C.noLink} />
        ) : null}
      </StickyColumn>
    </Layout>
  );
};

export default EmbedSettingsPanel;
